<template>
    <SortDropdown
        v-if="selectedSortItemId"
        :sort-order-items="sortOrderEntries"
        :selected-id="selectedSortItemId"
        @on-selection-changed="onSelectionChangedHandler"
    />
</template>

<script setup lang="ts">
import SortDropdown from "@/shared/components/sort-dropdown/sort-dropdown.vue";
import { SortEntry, SortOrderEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import { SortSaveKey, useSortSettings } from "@/shared/components/sort-dropdown/composables/sort-settings";
import { ref, toRefs, watch } from "vue";
import { watchImmediate } from "@vueuse/core";

const isE2E = location.href.includes("playwright");

const selectedSortEntry = defineModel<SortEntry | undefined>("selectedSortEntry", { required: true });
const props = defineProps<{
    sortOrderEntries: SortOrderEntry[];
    initialSelectedSortEntry?: SortEntry;
    sortSaveKey: SortSaveKey;
}>();

const selectedSortItemId = ref<string | undefined>();
const { sortOrderEntries } = toRefs(props);

const { update, sortOrderEntry } = useSortSettings(props.sortSaveKey, sortOrderEntries, props.initialSelectedSortEntry);

const isSameSort = (a: SortEntry, b: SortEntry) => a.field === b.field && a.order === b.order;

// last sort that is known to be persisted or was resolved on load (needs no saving)
let persistedSort: SortEntry | undefined;

const onSelectionChangedHandler = (newSelected: SortOrderEntry) => {
    selectedSortItemId.value = newSelected.id;
    selectedSortEntry.value = { field: newSelected.value.field, order: newSelected.value.order };
};

watchImmediate(sortOrderEntry, (orderEntry) => {
    if (!orderEntry) return;

    persistedSort = { field: orderEntry.value.field, order: orderEntry.value.order };
    onSelectionChangedHandler(orderEntry);
});

watch(selectedSortEntry, async (sortEntry) => {
    if (!sortEntry) return;

    const foundSortEntry = sortOrderEntries.value.find((item) => isSameSort(item.value, sortEntry));
    if (foundSortEntry) selectedSortItemId.value = foundSortEntry.id;

    //Don't save for e2e
    if (isE2E || (persistedSort && isSameSort(persistedSort, sortEntry))) return;

    persistedSort = { ...sortEntry };
    await update(persistedSort);
});
</script>
